import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { LoggingService } from '../services/logging.service';

/**
 * Global error handler that captures both Angular errors and window errors.
 * Provides consistent error logging and handling throughout the application.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  // List of error messages to ignore
  private readonly IGNORED_ERRORS = [
    'ResizeObserver loop limit exceeded',
  ];

  constructor(private injector: Injector) {
    this.setupWindowErrorHandler();
  }

  /**
   * Handle Angular-specific errors
   */
  handleError(error: Error): void {
    try {
      const loggingService = this.getLoggingService();
      loggingService.logException(error);
    } catch (handlingError) {
      console.error('Error in error handler:', handlingError);
    }
  }

  /**
   * Setup global window error handler
   */
  private setupWindowErrorHandler(): void {
    window.onerror = (event: Event | string, source?: string, lineno?: number, colno?: number, error?: Error): void => {
      this.handleWindowError(event, source, lineno, colno, error);
    };
  }

  /**
   * Process window-level errors
   */
  handleWindowError(event: Event | string, source?: string, lineno?: number, colno?: number, error?: Error): void {
    if (this.shouldIgnore(event)) {
      return;
    }

    try {
      const loggingService = this.getLoggingService();
      loggingService.logWindowError(event, source, lineno, colno, error);
    } catch (loggingError) {
      console.error('Failed to log window error:', loggingError);
    }
  }

  /**
   * Get the logging service from the injector
   */
  private getLoggingService(): LoggingService {
    return this.injector.get(LoggingService);
  }

  /**
   * Determine if an error should be ignored based on its message
   */
  private shouldIgnore(event: Event | string): boolean {
    if (typeof event !== 'string') {
      return false;
    }
    return this.IGNORED_ERRORS.includes(event);
  }
}
